import { useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { invalidateFor } from "@/lib/cache-registry";
import { useImpersonation } from "@/contexts/ImpersonationContext";

/**
 * VIB-2177: lest-status for prosjektoppdateringer (`project_comments`).
 *
 * IKKE person-til-person-chatten: `chat_members.last_read_at` er kun brukt
 * som mønster. Datakilden her er `project_comment_reads` + RPC-en
 * `project_comment_unread_summary` (én query for hele prosjektlisten).
 *
 * Aktiv bruker hentes via `useImpersonation()` (aldri `useAuth()`): under
 * impersonering skal badgen vise den impersonerte brukerens ulest-status.
 * RPC-en og RLS-policyene tillater dette for admin/superadmin.
 */

export interface ProjectCommentUnread {
  project_id: string;
  unread_count: number;
  has_mention: boolean;
}

export type ProjectCommentUnreadMap = Map<string, ProjectCommentUnread>;

/** Dedupliser + sorter så queryKey er stabil uavhengig av kallerens rekkefølge. */
export function normalizeProjectIds(projectIds: readonly string[]): string[] {
  return Array.from(new Set(projectIds.filter(Boolean))).sort();
}

/**
 * Ulest-oppsummering per prosjekt for aktiv bruker. Én RPC for hele listen,
 * ALDRI ett kall per prosjektrad.
 *
 * queryKey `["project-comment-unread", …]` er registrert i `CACHE_REGISTRY`
 * under både `project_comments` (ny kommentar) og `project_comment_reads`
 * (markert lest), uten entityField — se kommentaren i registry.ts.
 */
export function useProjectCommentUnread(
  projectIds: readonly string[],
  options: { enabled?: boolean; userId?: string | null } = {},
) {
  const { activeUser } = useImpersonation();
  // Chat-rommet (VIB-3024) sender den ekte sesjonen; ellers aktiv bruker.
  const userId = options.userId !== undefined ? options.userId : activeUser?.id ?? null;
  const idKey = normalizeProjectIds(projectIds).join(",");
  const ids = useMemo(() => (idKey ? idKey.split(",") : []), [idKey]);
  const enabled = (options.enabled ?? true) && !!userId && ids.length > 0;

  return useQuery({
    queryKey: ["project-comment-unread", { userId, projectIds: ids }],
    enabled,
    queryFn: async (): Promise<ProjectCommentUnreadMap> => {
      const { data, error } = await supabase.rpc("project_comment_unread_summary", {
        p_project_ids: ids,
        p_user_id: userId,
      });
      if (error) throw error;
      const map: ProjectCommentUnreadMap = new Map();
      for (const row of (data ?? []) as ProjectCommentUnread[]) {
        map.set(row.project_id, row);
      }
      return map;
    },
    // Realtime på project_comments invaliderer ved ny kommentar; staleTime
    // holder listesidene fra å re-kjøre RPC-en på hver fokus-endring.
    staleTime: 30 * 1000,
  });
}

/**
 * Markerer alle prosjektoppdateringer for et prosjekt som lest for aktiv
 * bruker (upsert `last_read_at = now()`). Kalles når dialogen åpnes og lukkes.
 */
export function useMarkProjectCommentsRead(userIdOverride?: string | null) {
  const qc = useQueryClient();
  const { activeUser } = useImpersonation();
  // Chat-rommet (VIB-3024) markerer for den ekte sesjonen; ellers aktiv bruker.
  const userId = userIdOverride !== undefined ? userIdOverride : activeUser?.id ?? null;

  return useMutation({
    mutationFn: async (input: { projectId: string }) => {
      if (!userId) return;
      const { error } = await supabase.from("project_comment_reads").upsert(
        {
          user_id: userId,
          project_id: input.projectId,
          last_read_at: new Date().toISOString(),
        },
        { onConflict: "user_id,project_id" },
      );
      if (error) throw error;
    },
    onSuccess: async (_data, vars) => {
      await invalidateFor(qc, "project_comment_reads", { projectId: vars.projectId });
    },
    // Ingen toast ved feil med hensikt: en badge som henger igjen er
    // mindre støy enn en feilmelding hver gang dialogen åpnes uten nett.
  });
}
